import React, { useEffect, useState } from 'react';
import { getAuditLogs } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  History,
  User,
  Clock,
  FileText,
  Briefcase,
  Upload,
  Trash2,
  Edit,
  LogIn,
  Key
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const actionIcons = {
  CREATE: Edit,
  UPDATE: Edit,
  DELETE: Trash2,
  UPLOAD: Upload,
  LOGIN: LogIn,
  PASSWORD_CHANGE: Key
};

const actionColors = {
  CREATE: 'bg-green-100 text-green-600',
  UPDATE: 'bg-blue-100 text-blue-600',
  DELETE: 'bg-red-100 text-red-600',
  UPLOAD: 'bg-purple-100 text-purple-600',
  LOGIN: 'bg-amber-100 text-amber-600',
  PASSWORD_CHANGE: 'bg-cyan-100 text-cyan-600'
};

const entityIcons = {
  user: User,
  job: Briefcase,
  document: FileText
};

const AuditLogs = () => {
  const { t, locale } = useLanguage();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const data = await getAuditLogs();
        setLogs(data);
      } catch (error) {
        console.error('Failed to fetch audit logs:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchLogs();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-[#1F3D2B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[#2E2E2E]" data-testid="admin-audit-title">{t('portalUi.admin.audit.title', 'Audit logs')}</h1>
        <p className="text-[#2E2E2E]/60 mt-1" data-testid="admin-audit-subtitle">{t('portalUi.admin.audit.subtitle', 'Sistēmas darbību vēsture')}</p>
      </div>

      {/* Logs */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-6">
          {logs.length > 0 ? (
            <div className="space-y-4">
              {logs.map((log) => {
                const ActionIcon = actionIcons[log.action] || Edit;
                const EntityIcon = entityIcons[log.entity_type] || FileText;
                
                return (
                  <div
                    key={log.id}
                    className="flex items-start gap-4 p-4 rounded-xl bg-[#f8faf8] hover:bg-[#1F3D2B]/5 transition-colors"
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${actionColors[log.action] || 'bg-gray-100 text-gray-600'}`}>
                      <ActionIcon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-[#2E2E2E]">{log.user_name}</span>
                        <span className="text-[#2E2E2E]/40">•</span>
                        <span className="text-sm text-[#2E2E2E]/60">{log.action}</span>
                        <div className="flex items-center gap-1 text-sm text-[#2E2E2E]/60">
                          <EntityIcon className="w-3 h-3" />
                          {log.entity_type}
                        </div>
                      </div>
                      {log.details && (
                        <p className="text-sm text-[#2E2E2E]/70">{log.details}</p>
                      )}
                      <div className="flex items-center gap-1 mt-2 text-xs text-[#2E2E2E]/40">
                        <Clock className="w-3 h-3" />
                        {new Date(log.created_at).toLocaleString(locale)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12" data-testid="admin-audit-empty">
              <History className="w-16 h-16 mx-auto mb-4 text-[#2E2E2E]/20" />
              <h3 className="text-lg font-semibold text-[#2E2E2E] mb-2">{t('portalUi.admin.audit.emptyTitle', 'Nav audit log ierakstu')}</h3>
              <p className="text-[#2E2E2E]/60">{t('portalUi.admin.audit.emptyBody', 'Darbību vēsture parādīsies šeit')}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLogs;
